// /settings/tax — Tax Configuration (PAGES.md §3.77)
//
// GST settings: default slab, inclusive/exclusive pricing. The "Default
// Transaction Type" (intra/inter-state) radio is retired (gst-parked D7): the
// GST resolver decides CGST+SGST vs IGST from the GSTIN / State.
// SHARED vertical. OWNER+ (50). Traceability: FR-BILL-008, FR-BILL-015

import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Select,
  Toggle,
  Alert,
  Input,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { tenantService } from '@/services/tenant.service';
import { ApiError } from '@/lib/api-client';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { getStandardProductTaxRate } from '@bbpos/types';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';

// ─── Constants ─────────────────────────────────────────────────────────────

const GST_SLAB_OPTIONS = [
  { value: '0', label: '0% (Exempt)' },
  { value: '5', label: '5%' },
  { value: '12', label: '12%' },
  { value: '18', label: '18% (Standard)' },
  { value: '28', label: '28% (Luxury)' },
];

/**
 * The rates a tenant may pick as its default, mirroring the API's
 * regime check (QA 3.8.4): India — the five GST slabs; GCC VAT — zero-rated
 * or the country standard rate (UAE/Oman 5, Bahrain 10, Saudi 15);
 * Kuwait/Qatar — 0 only.
 */
function rateOptionsFor(isIndia: boolean, standardRate: number) {
  if (isIndia) return GST_SLAB_OPTIONS;
  if (standardRate <= 0) return [{ value: '0', label: '0% (No VAT)' }];
  return [
    { value: '0', label: '0% (Zero-rated / Exempt)' },
    { value: String(standardRate), label: `${standardRate}% (Standard)` },
  ];
}

/** The three settings as stored. `slab` is null until a default rate is saved. */
interface SavedTax {
  slab: string | null;
  inclusive: boolean;
  hsn: string;
}

function savedTaxFrom(d: Record<string, unknown>): SavedTax {
  return {
    slab: d.default_tax_slab == null ? null : String(d.default_tax_slab),
    inclusive: d.show_tax_inclusive === true,
    hsn: String(d.default_hsn_code ?? ''),
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

function TaxConfigContent() {
  const { toast } = useToast();
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? '');
  // Tax vocabulary/visibility derives from the tenant's immutable currency.
  // INR → GST_INDIA: split CGST/SGST/IGST + HSN + `Math.floor` rounding, every
  // string byte-identical to the legacy India-only copy. GCC → single VAT line:
  // no inter/intra split, no HSN, half-up rounding.
  const tax = useTaxRegime();
  const currency = useTenantCurrency();
  const isIndia = tax.regime === 'GST_INDIA';
  // India has no single standard rate (per-slab sentinel); 18% is the legacy default.
  const standardRate = isIndia ? 18 : getStandardProductTaxRate(currency);
  const rateOptions = rateOptionsFor(isIndia, standardRate);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultSlab, setDefaultSlab] = useState(String(standardRate));
  const [showTaxInclusive, setShowTaxInclusive] = useState(false);
  const [defaultHsn, setDefaultHsn] = useState('');
  // What the server holds, so Save is off until something changes and off
  // again after saving, like the other settings pages (I-5).
  const [saved, setSaved] = useState<SavedTax | null>(null);

  const applySaved = useCallback((s: SavedTax) => {
    setSaved(s);
    setDefaultSlab(s.slab ?? String(standardRate));
    setShowTaxInclusive(s.inclusive);
    setDefaultHsn(s.hsn);
  }, [standardRate]);

  // A slab never saved (null) compares as the standard rate it shows, so an
  // untouched page is not "Unsaved changes" and leaving it asks nothing (T1-001);
  // Save still stays on so the owner can store it as the default.
  // HSN compares trimmed, as sent.
  const isDirty =
    saved !== null &&
    ((saved.slab ?? String(standardRate)) !== defaultSlab ||
      saved.inclusive !== showTaxInclusive ||
      saved.hsn !== defaultHsn.trim());
  const canSave = isDirty || saved?.slab === null;
  const { dialog: leaveDialog } = useUnsavedChangesGuard(isDirty);

  useEffect(() => { document.title = 'Tax Configuration — Settings — Orio360 Admin'; }, []);

  // Fetch current settings
  useEffect(() => {
    if (!tenantId) return;
    setIsLoading(true);
    tenantService.getById(tenantId).then((data) => {
      // Extended tax fields may live in a settings sub-object.
      // Cast via unknown to access optional fields safely.
      applySaved(savedTaxFrom(data as unknown as Record<string, unknown>));
    }).catch(() => {
      toast({ title: 'Failed to load tax settings', variant: 'error' });
    }).finally(() => setIsLoading(false));
  }, [tenantId, applySaved, toast]);

  const handleSave = useCallback(async () => {
    if (!tenantId) return;
    setIsSaving(true);
    try {
      const res = await tenantService.updateTaxSettings(tenantId, {
        default_tax_slab: Number(defaultSlab),
        show_tax_inclusive: showTaxInclusive,
        default_hsn_code: defaultHsn.trim() || undefined,
      });
      // Re-read the fields and the baseline from what the server stored.
      applySaved(savedTaxFrom(res as unknown as Record<string, unknown>));
      toast({ title: 'Tax configuration updated', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [tenantId, defaultSlab, showTaxInclusive, defaultHsn, applySaved, toast]);

  // Keyboard: Ctrl+S — mirror the Save button's guard (no-op when pristine or already saving)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (!canSave || isSaving) return;
        handleSave();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, canSave, isSaving]);

  return (
    <PageContainer
      title="Tax Configuration"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Tax' },
      ]}
    >
      <div className="animate-fade-in">
        {/* Back link */}
        <Link to="/settings" className="text-sm text-brand-600 dark:text-brand-400 hover:underline mb-4 inline-block">
          ← Back to Settings
        </Link>

        {/* Header */}
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Tax Configuration</h1>
            <p className="text-neutral-300 mt-1">
              {tax.splitLabels
                ? 'GST slabs and pricing mode settings'
                : `${tax.taxName} rate and pricing mode settings`}
            </p>
          </div>
        </div>

        {isLoading ? (
          <Skeleton variant="rect" height={400} className="rounded-2xl" />
        ) : (
          <div className={cn(GLASS_FORM, 'p-5 sm:p-6 animate-slide-up')}>
            <div className="space-y-6 max-w-xl">
              {/* Default GST Slab */}
              <div>
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                  {tax.splitLabels ? 'Default GST Slab (%)' : `Default ${tax.taxName} Rate (%)`}
                </label>
                <Select
                  options={rateOptions}
                  value={defaultSlab}
                  onChange={(v) => setDefaultSlab(typeof v === 'string' ? v : v[0] ?? String(standardRate))}
                />
                <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">
                  Applied to new products when no specific slab is set
                </p>
              </div>

              {/* CGST + SGST or IGST — India only. No longer a setting (D7):
                  it follows the GSTIN and State of the business and each store. */}
              {tax.splitLabels && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Orio360 picks CGST + SGST or IGST for each sale from your GSTIN and State and the
                  store&apos;s State. Set them in{' '}
                  <Link to="/settings" className="font-semibold text-brand-600 dark:text-brand-400 hover:underline">
                    Business Settings
                  </Link>
                  .
                </p>
              )}

              {/* Tax-Inclusive Toggle */}
              <Toggle
                checked={showTaxInclusive}
                onChange={setShowTaxInclusive}
                label="Display Tax-Inclusive Prices"
                description="Show prices with tax included on POS and receipts"
                className="w-full"
              />

              {/* Default HSN Code — India-only statutory surface; GCC VAT has no
                  HSN/SAC classification, so this is hidden. */}
              {tax.showHsn && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Default HSN Code</label>
                  <Input
                    value={defaultHsn}
                    onChange={setDefaultHsn}
                    placeholder="e.g. 9963"
                    className="font-mono"
                  />
                </div>
              )}

              {/* Tax Rounding Info */}
              <div>
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Tax Rounding Method</label>
                <div className="flex items-center gap-2 mt-1">
                  {tax.lineRounding === 'floor' ? (
                    <span className="text-sm font-mono font-semibold text-neutral-700 dark:text-neutral-300" aria-label="Tax rounding always uses Math.floor() per line item">
                      Math.floor()
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300" aria-label="Tax rounding always uses round-half-up per line item">
                      Round half-up
                    </span>
                  )}
                  <span className="text-[10px] text-neutral-400 dark:text-neutral-500">Non-configurable</span>
                </div>
              </div>

              {/* Compliance Alert */}
              <Alert
                variant="info"
                title={`${tax.taxName} Compliance`}
                description={
                  tax.lineRounding === 'floor'
                    ? 'Tax rounding always uses Math.floor() per line item in paise. This is non-configurable per Indian GST compliance rules. All amounts are stored as paise (1 INR = 100 paise).'
                    : `Tax rounding always uses round-half-up per line item to the smallest currency unit. This is non-configurable per ${tax.taxName} compliance rules. All amounts are stored as integer minor units.`
                }
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <SaveStatus dirty={isDirty} />
                <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !canSave}>
                  {isSaving ? 'Saving...' : 'Save Tax Settings'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {leaveDialog}
    </PageContainer>
  );
}

export function TaxConfigPage() {
  return (
    <ErrorBoundary>
      <TaxConfigContent />
    </ErrorBoundary>
  );
}
